import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../constants.js';

export default class CreditsScene extends Phaser.Scene {
  constructor() {
    super({ key: 'CreditsScene' });
    this.creditsGroup = null;
    this.scrollSpeed = 0.8;
    this.music = null;
  }

  create() {
    // Black background with slight purple tint (80s aesthetic)
    this.cameras.main.setBackgroundColor('#0a0a1a');

    // Create CRT scanline effect
    this.createScanlines();

    // Create starfield background effect
    this.createStarfield();

    // Start credits music (reuse cutscene music for now)
    this.music = this.sound.add('cutscene_music', {
      loop: true,
      volume: 0.4,
    });
    this.music.play();

    // Create container for scrolling credits
    this.creditsGroup = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT + 50);

    // Build credits content
    const credits = this.buildCredits();

    // Add all credit elements to the container
    let yOffset = 0;
    credits.forEach(item => {
      const textObj = this.add.text(0, yOffset, item.text, {
        fontFamily: 'Courier New',
        fontSize: item.size || '18px',
        fill: item.color || '#ffffff',
        align: 'center',
        stroke: '#000000',
        strokeThickness: 2,
      });
      textObj.setOrigin(0.5, 0);
      this.creditsGroup.add(textObj);
      yOffset += item.spacing || 35;
    });

    // Store total height for scroll calculation
    this.totalCreditsHeight = yOffset;

    // Add flashing "GAME OVER" at top (stays fixed)
    const gameOverText = this.add.text(GAME_WIDTH / 2, 40, 'MISSION COMPLETE', {
      fontFamily: 'Courier New',
      fontSize: '32px',
      fill: '#00ff00',
      stroke: '#000000',
      strokeThickness: 4,
    });
    gameOverText.setOrigin(0.5);
    gameOverText.setDepth(100);

    // Pulse the game over text
    this.tweens.add({
      targets: gameOverText,
      alpha: { from: 1, to: 0.5 },
      duration: 800,
      yoyo: true,
      repeat: -1,
    });

    // Skip instruction at bottom
    const skipText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 25, 'PRESS ENTER TO CONTINUE', {
      fontFamily: 'Courier New',
      fontSize: '14px',
      fill: '#ffff00',
      stroke: '#000000',
      strokeThickness: 2,
    });
    skipText.setOrigin(0.5);
    skipText.setDepth(100);

    // Blink the skip text
    this.tweens.add({
      targets: skipText,
      alpha: { from: 1, to: 0.3 },
      duration: 500,
      yoyo: true,
      repeat: -1,
    });

    // Input handlers
    this.input.keyboard.on('keydown-ENTER', () => this.exitCredits());
    this.input.keyboard.on('keydown-SPACE', () => this.exitCredits());
    this.input.keyboard.on('keydown-ESC', () => this.exitCredits());
    this.input.on('pointerdown', () => this.exitCredits());

    // Fade in
    this.cameras.main.fadeIn(1000, 0, 0, 0);
  }

  buildCredits() {
    return [
      // Title card
      { text: '- - - - - - - - - - - - - - -', color: '#ff00ff', size: '16px', spacing: 40 },
      { text: '', spacing: 30 },

      // Company logo text
      { text: '1 V 1   S T U D I O S', color: '#00ffff', size: '36px', spacing: 50 },
      { text: 'P R E S E N T S', color: '#ff00ff', size: '20px', spacing: 80 },

      // Divider
      { text: '* * * * * * * * * * * * * * *', color: '#ffff00', size: '14px', spacing: 50 },
      { text: '', spacing: 30 },

      // Lead Developer
      { text: 'LEAD DEVELOPER', color: '#00ff00', size: '14px', spacing: 25 },
      { text: 'Hayden "Sniper" B', color: '#ffffff', size: '24px', spacing: 60 },

      // General Manager
      { text: 'GENERAL MANAGER', color: '#00ff00', size: '14px', spacing: 25 },
      { text: 'Graham "Rad Bad Dad" B', color: '#ffffff', size: '24px', spacing: 60 },

      // Level Editor
      { text: 'LEVEL EDITOR', color: '#00ff00', size: '14px', spacing: 25 },
      { text: 'Hayden "Sniper" B', color: '#ffffff', size: '24px', spacing: 60 },

      // Storyline
      { text: 'STORYLINE', color: '#00ff00', size: '14px', spacing: 25 },
      { text: 'Graham "Rad Bad Dad" B', color: '#ffffff', size: '24px', spacing: 60 },

      // Divider
      { text: '- - - - - - - - - - - - - - -', color: '#ff00ff', size: '16px', spacing: 50 },
      { text: '', spacing: 20 },

      // Audio Effects
      { text: 'AUDIO EFFECTS', color: '#00ff00', size: '14px', spacing: 25 },
      { text: 'The Motion Monkey', color: '#ffffff', size: '24px', spacing: 35 },
      { text: 'themotionmonkey.co.uk', color: '#888888', size: '14px', spacing: 80 },

      // Divider
      { text: '* * * * * * * * * * * * * * *', color: '#ffff00', size: '14px', spacing: 50 },
      { text: '', spacing: 30 },

      // Copyright
      { text: 'COPYRIGHT 2026', color: '#ff6600', size: '18px', spacing: 30 },
      { text: '1v1studios', color: '#ff6600', size: '22px', spacing: 35 },
      { text: '1v1studios.com', color: '#888888', size: '14px', spacing: 80 },

      // Big divider before dedication
      { text: '', spacing: 40 },
      { text: '= = = = = = = = = = = = = = =', color: '#ff00ff', size: '16px', spacing: 40 },
      { text: '', spacing: 60 },

      // Dedication (special styling)
      { text: 'D E D I C A T E D   T O', color: '#ff6699', size: '16px', spacing: 30 },
      { text: '', spacing: 20 },
      { text: 'J E S S I E', color: '#ff6699', size: '42px', spacing: 60 },
      { text: '', spacing: 60 },

      // Final flourish
      { text: '= = = = = = = = = = = = = = =', color: '#ff00ff', size: '16px', spacing: 80 },
      { text: '', spacing: 100 },

      // Thanks for playing
      { text: 'THANKS FOR PLAYING!', color: '#00ffff', size: '28px', spacing: 50 },
      { text: '', spacing: 100 },
    ];
  }

  createScanlines() {
    const graphics = this.add.graphics();
    graphics.setDepth(200);
    graphics.setScrollFactor(0);

    // Draw horizontal scanlines
    for (let y = 0; y < GAME_HEIGHT; y += 3) {
      graphics.fillStyle(0x000000, 0.12);
      graphics.fillRect(0, y, GAME_WIDTH, 1);
    }
  }

  createStarfield() {
    // Create multiple layers of stars for parallax effect
    this.stars = [];

    for (let i = 0; i < 80; i++) {
      const x = Phaser.Math.Between(0, GAME_WIDTH);
      const y = Phaser.Math.Between(0, GAME_HEIGHT);
      const size = Phaser.Math.Between(1, 3);
      const alpha = Phaser.Math.FloatBetween(0.3, 1);

      const star = this.add.circle(x, y, size, 0xffffff, alpha);
      star.setDepth(0);
      star.speed = size * 0.3; // Larger stars move faster (parallax)
      this.stars.push(star);
    }

    // Add some colored stars for 80s flair
    for (let i = 0; i < 15; i++) {
      const x = Phaser.Math.Between(0, GAME_WIDTH);
      const y = Phaser.Math.Between(0, GAME_HEIGHT);
      const colors = [0xff00ff, 0x00ffff, 0xffff00, 0xff6600];
      const color = Phaser.Utils.Array.GetRandom(colors);

      const star = this.add.circle(x, y, 2, color, 0.8);
      star.setDepth(0);
      star.speed = 0.5;
      this.stars.push(star);

      // Make colored stars twinkle
      this.tweens.add({
        targets: star,
        alpha: { from: 0.8, to: 0.2 },
        duration: Phaser.Math.Between(500, 1500),
        yoyo: true,
        repeat: -1,
      });
    }
  }

  update() {
    // Scroll credits upward
    if (this.creditsGroup) {
      this.creditsGroup.y -= this.scrollSpeed;

      // Check if credits have fully scrolled off screen
      if (this.creditsGroup.y < -this.totalCreditsHeight - 100) {
        // Reset to bottom and keep looping
        this.creditsGroup.y = GAME_HEIGHT + 50;
      }
    }

    // Animate starfield
    this.stars.forEach(star => {
      star.y -= star.speed * 0.5;
      if (star.y < -5) {
        star.y = GAME_HEIGHT + 5;
        star.x = Phaser.Math.Between(0, GAME_WIDTH);
      }
    });
  }

  exitCredits() {
    // Clean up
    this.input.keyboard.off('keydown-ENTER');
    this.input.keyboard.off('keydown-SPACE');
    this.input.keyboard.off('keydown-ESC');
    this.input.off('pointerdown');

    // Fade out music
    if (this.music) {
      this.tweens.add({
        targets: this.music,
        volume: 0,
        duration: 800,
      });
    }

    // Fade out and return to menu
    this.cameras.main.fadeOut(800, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      if (this.music) {
        this.music.stop();
      }
      this.scene.start('MenuScene');
    });
  }
}
